import { useMemo, useState } from 'react';
import { Search, SlidersHorizontal } from 'lucide-react';
import {
  POS_CATEGORY_ICONS,
  getPosCategoryIcon,
  industryIconGroup,
} from '@/constants/posCategoryIcons';
import { useTranslation } from 'react-i18next';

interface Props {
  value?: string | null;
  onChange: (name: string) => void;
  /** Industry label (e.g. "Groceries & Supermarket") to recommend icons for. */
  industry?: string | null;
}

export default function IconPicker({ value, onChange, industry }: Props) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [showRecommendedOnly, setShowRecommendedOnly] = useState(Boolean(industry));

  const group = industryIconGroup(industry);

  const recommended = useMemo(() => {
    if (!group) return POS_CATEGORY_ICONS;
    const matched = POS_CATEGORY_ICONS.filter((e) => e.industries?.includes(group));
    // Always keep generic icons available in the recommended list too.
    const generic = POS_CATEGORY_ICONS.filter((e) => !e.industries || e.industries.length === 0);
    return [...matched, ...generic];
  }, [group]);

  const base = group && showRecommendedOnly ? recommended : POS_CATEGORY_ICONS;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return base;
    return base.filter((entry) => {
      const label = t(entry.labelKey, { defaultValue: entry.name }).toLowerCase();
      return entry.name.toLowerCase().includes(q) || label.includes(q);
    });
  }, [search, t, base]);

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('common.search')}
            className="input-field pl-9 w-full text-sm"
          />
        </div>
        {group && (
          <button
            type="button"
            onClick={() => setShowRecommendedOnly((v) => !v)}
            className={`flex items-center gap-1.5 px-2.5 py-2 rounded-lg border text-xs font-medium whitespace-nowrap transition-colors ${
              showRecommendedOnly
                ? 'border-primary-500 bg-primary-50 text-primary-700'
                : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
            }`}
            title={t('starterCatalog.recommendedIcons', { defaultValue: 'Recommended for your industry' })}
          >
            <SlidersHorizontal className="w-3.5 h-3.5" />
            {t('starterCatalog.recommended', { defaultValue: 'Recommended' })}
          </button>
        )}
      </div>
      <div className="grid grid-cols-8 lg:grid-cols-10 gap-1.5 max-h-56 overflow-y-auto p-1">
        {filtered.map((entry) => {
          const Icon = entry.icon;
          const selected = value === entry.name;
          return (
            <button
              key={entry.name}
              type="button"
              onClick={() => onChange(entry.name)}
              title={t(entry.labelKey, { defaultValue: entry.name })}
              className={`flex flex-col items-center justify-center gap-1 p-2 rounded-lg border transition-colors ${
                selected
                  ? 'border-primary-500 bg-primary-50 text-primary-700'
                  : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Icon className="w-5 h-5" />
            </button>
          );
        })}
      </div>
      {filtered.length === 0 && (
        <p className="text-xs text-gray-500 text-center py-2">{t('common.noResults')}</p>
      )}
    </div>
  );
}

export { getPosCategoryIcon };
